import { HttpClient, HttpClientModule, HttpHeaders } from '@angular/common/http';
import { Injectable, NgModule } from '@angular/core';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { SpParams } from '../models/share-point';
import { HasMetadata, SharePointService, SpGetOptions } from './share-point';

export interface Group {
  [name: string]: boolean;
}

export interface User extends HasMetadata {
  Groups: Groups;
  Id: number;
  LoginName: string;
  Title: string;
  PrincipalType: number;
  Email: string;
  IsSiteAdmin: boolean;
  UserPrincipalName: string;
}

export interface Groups {
  results: GroupResult[];
}

export interface UpdatedGroup {
  removed: boolean;
  group: {
    Title: string
  };
}

interface GroupResult extends HasMetadata {
  Id: number;
  Title: string;
}

interface Alerts {
  __deferred: Deferred;
}

interface Deferred {
  uri: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private userSubscription$: Observable<User>;
  private userGroupsSubscription$: Observable<Group>;
  private readonly baseParams: SpParams = {
    $select: 'Id,LoginName,Title,Email,IsSiteAdmin,UserPrincipalName,Groups/Id,Groups/Title',
    $expand: 'Groups'
  };

  private spGetOptions: SpGetOptions = {
    service: true,
    params: this.baseParams,
  };

  private spSearchOptions: SpGetOptions = {
    service: true,
    params: this.baseParams,
  };

  constructor(
    private http: HttpClient,
    private spService: SharePointService,
    private toaster: ToastrService
  ) { }

  /**
   * @return an `Observable` for the current user.
   */

  public current(options?: SpGetOptions): Observable<User> {
    if (!this.userSubscription$) {
      this.userSubscription$ = this.spService.get<User>('CurrentUser', { ...this.spGetOptions, ...options }).pipe(
        map(results => results[0]),
        catchError(() => of({ Groups: { results: [] } } as User)),
        shareReplay({ refCount: false, bufferSize: 1 })
      );
    }

    return this.userSubscription$;
  }

  /**
   * @param searchFor as `string` for first name, last name, email, or employee number
   * @return an `Observable` array of users that match the search request.
   */

  public search(searchFor: string, options?: SpGetOptions): Observable<User[]> {
    const camelize = function (searchForString: string) {
      return searchForString.replace(/(\w)(\w*)/g, (_, firstLetter, remainder) => firstLetter.toUpperCase() + remainder.toLowerCase());
    };
    const params = [];
    params.push(`substringof('${camelize(searchFor)}', UserPrincipalName)`);
    params.push(`substringof('${camelize(searchFor)}', Title)`);
    params.push(`substringof('${camelize(searchFor)}', Email)`);
    this.spSearchOptions.params.$filter = `Email ne '' and (${params.join(' or ')})`;
    return this.spService.get<User>('SiteUsers', { ...this.spSearchOptions, ...options });
  }

  /**
   * @param group as `string` group name to add to or delete from
   * @param loginName as `string` users login name
   * @param remove as `bool` remove user from group
   */

  public updateGroup(group: string, loginName: string, remove: boolean, headers?: HttpHeaders): Observable<UpdatedGroup> {
    return this.spService.digest({}, {}).pipe(
      switchMap(digest => this.http.post(
        `_api/web/sitegroups/getbyname('${group}')/users${remove ? '/removeByLoginName' : ''}`,
        { [remove ? 'loginName' : 'LoginName']: loginName },
        { headers: { 'X-RequestDigest': digest, ...headers } }
      )),
      map(() => {
        return {
          removed: remove,
          group: {
            Title: group,
          }
        };
      }),
      tap(() => this.toaster.success('Updated')),
      catchError(error => {
        this.toaster.error('While updating permission', 'Error');
        return throwError(error);
      })
    );
  }

  /**
   * userGroups
   */
  public userGroups() {
    if (!this.userGroupsSubscription$) {
      this.userGroupsSubscription$ = this.current().pipe(
        map(user => {
          const groupObj = {};
          (user as User).Groups.results.forEach(reoupResult => groupObj[reoupResult.Title] = true);
          return groupObj as Group;
        }),
        shareReplay({ refCount: false, bufferSize: 1 })
      );
    }
    return this.userGroupsSubscription$;
  }
}

@NgModule({
  imports: [
    HttpClientModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
      preventDuplicates: true
    }),
  ],
  providers: [
    UserService,
  ],
  exports: [
    ToastrModule
  ]
})
export class UserModule { }
